// ============================================
// Meta Pixel Helper for Storify Now
// ============================================

import ReactPixel from 'react-facebook-pixel';

const PIXEL_ID = '733634833162733';

/*
 * Environment switch, so local development never sends events to the live
 * pixel. Set REACT_APP_ENABLE_PIXEL="true" in the Vercel project settings;
 * leave it "false" (or unset) in your local .env. Flip it locally only when
 * you actually want to test the pixel itself.
 */
const PIXEL_ENABLED = process.env.REACT_APP_ENABLE_PIXEL === 'true';

/*
 * Consent gate.
 *
 * ReactPixel.init() is what injects fbevents.js and sets the _fbp cookie,
 * so we simply never call it until the visitor has accepted marketing
 * cookies — no script, no cookie, no request to Meta before consent.
 *
 * Every track helper below also checks this flag, so a stray call from a
 * component can't leak an event. Do not call ReactPixel directly, and do
 * not load the pixel from index.html — a script in <head> runs before the
 * consent banner exists.
 */
let pixelReady = false;

// Called by the consent layer once marketing consent is granted.
export const initPixel = () => {
  if (pixelReady) return;

  if (!PIXEL_ENABLED) {
    // Consent was given but the environment has tracking switched off.
    // Logged so a missing env var in production is discoverable.
    console.info('[metaPixel] consent granted but disabled via REACT_APP_ENABLE_PIXEL');
    return;
  }

  ReactPixel.init(PIXEL_ID);
  pixelReady = true;
};

// Called when consent is refused or withdrawn. The script cannot be
// unloaded mid-session, so we tell Meta to stop and close our own gate;
// on the next page load init() is never reached at all.
export const revokePixel = () => {
  if (!pixelReady) return;
  ReactPixel.revokeConsent();
  pixelReady = false;
};

export const isPixelActive = () => pixelReady;

const track = (event, data) => {
  if (!pixelReady) return;
  ReactPixel.track(event, data);
};

/*
 * PageView used to come from the inline script in index.html, which fired
 * exactly once per full page load — meaning every in-app navigation went
 * uncounted. Call this on route changes instead. No-ops until consent.
 */
export const trackPageView = () => {
  if (!pixelReady) return;
  ReactPixel.pageView();
};

// Call this ONLY when a new account is actually created — not on sign-in,
// or every returning user inflates the conversion count.
export const trackCompleteRegistration = (method = 'email') => {
  track('CompleteRegistration', {
    content_name: 'account_created',
    registration_method: method,
    status: true,
  });
};

// Call this when a user views a book page
// Usage: trackViewBook('My Book Title')
export const trackViewBook = (bookName) => {
  track('ViewContent', {
    content_name: bookName,
    content_type: 'product',
  });
};

// Call this when a user adds a book to cart
// Usage: trackAddToCart('My Book Title', 49.99)
export const trackAddToCart = (bookName, price) => {
  track('AddToCart', {
    content_name: bookName,
    value: price,
    currency: 'RON',
  });
};

// Call this AFTER a purchase is confirmed by your backend
// Usage: trackPurchase('My Book Title', 49.99)
export const trackPurchase = (bookName, price, currency = 'RON') => {
  track('Purchase', {
    content_name: bookName,
    value: price,
    currency: currency,
  });
};

// Call this when a user views the promo landing page
export const trackPromoView = (country) => {
  track('ViewContent', {
    content_name: 'promo_landing',
    content_category: 'landing_page',
    country: country,
  });
};

// Call this when a user clicks a CTA on the landing page
export const trackPromoCta = (ctaLocation) => {
  track('ViewContent', {
    content_name: 'promo_cta_click',
    content_category: ctaLocation,
  });
};
